"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import Cookie from "js-cookie";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";

type AuthContextType = {
  user: User;
  userToken: string;
  isLoading: boolean;
  isLoggedIn: boolean;
  login: (data: AuthResponse) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  subscription: boolean;
};

const AuthContext =
  createContext<Partial<AuthContextType | undefined>>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const router = useRouter();
  const initUser = useMemo(
    () => ({
      id: "",
      username: "",
      email: "",
      name: "",
      role: "user" as UserRole,
      image: "",
      profile_link: "",
      courses_count: 0,
      learning_language: "en-GB",
      group: "",
      coach: { id: "", name: "", image: "" },
      lisa_version: 0,
      is_nll: false,
      onboarding_completed: false,
      white_lable_logo: "",
    }),
    [],
  );

  const [user, setUser] = useState(initUser);
  const [isLoading, setIsLoading] = useState(user.id === "");
  const [isLoggedIn, setIsLoggedIn] = useState(user.id !== "");
  const queryClient = useQueryClient();
  const [subscription, setSubscription] = useState<boolean>(false);

  const checkSubscriptionStatus = useCallback(async () => {
    const token = Cookie.get("access_token");
    if (!token) {
      setIsLoggedIn(false);
      return;
    }
    try {
      const response = await fetch(
        process.env.NEXT_PUBLIC_API_URL + "/billing/check-subscription",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();
      setSubscription(data.data.isActive);
    } catch (error) {
      setSubscription(false);
    }
  }, []);

  const updateLocalStorageLogo = (key: string, newValue: string) => {
    const current = localStorage.getItem(key);

    if (newValue && current !== newValue) {
      localStorage.setItem(key, newValue);
    }

    if (!newValue) {
      localStorage.removeItem(key);
    }
  };

  const saveUserProfile = useCallback(async () => {
    const token = Cookie.get("access_token");
    if (!token) {
      setIsLoggedIn(false);
      return;
    }
    const userRes = await fetch(
      process.env.NEXT_PUBLIC_API_URL + "/user/profile",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (userRes.ok) {
      const result = await userRes.json();
      const userResult = result.data;
      userResult.userToken = token;
      // update role cookie
      Cookie.set("role", userResult.role, {
        expires: 180,
        sameSite: "None",
        secure: true,
      });

      if (userResult) {
        updateLocalStorageLogo("logo", userResult?.white_label_logo);
        updateLocalStorageLogo("logo_alt", userResult?.white_label_name);
      }

      setUser(userResult);
      setIsLoggedIn(true);

      // Set user role
      await checkSubscriptionStatus();
    }
  }, [checkSubscriptionStatus]);

  const clearUserProfile = useCallback(() => {
    setUser(initUser);
  }, [initUser]);

  useEffect(() => {
    if (isLoading && !isLoggedIn && user.id === "") {
      (async () => {
        await saveUserProfile();
        setIsLoading(false);
      })();
    }
  }, [isLoading, isLoggedIn, user, saveUserProfile]);

  const login = useCallback(
    async (data: AuthResponse) => {
      Cookie.set("access_token", data.access_token, {
        expires: 180,
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN || undefined,
        sameSite: "None",
        secure: true,
      });
      Cookie.set("role", data.user.role, {
        expires: 180,
        sameSite: "None",
        secure: true,
      });
      await saveUserProfile();
      await checkSubscriptionStatus();

      setIsLoggedIn(true);
    },
    [checkSubscriptionStatus, saveUserProfile],
  );

  const refreshProfile = useCallback(async () => {
    await saveUserProfile();
  }, [saveUserProfile]);

  const logout = useCallback(() => {
    // Remove cookies
    Cookie.remove("access_token");
    Cookie.remove("role");

    // Remove cookies with domain
    Cookie.remove("access_token", {
      domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN || undefined,
    });
    Cookie.remove("role", {
      domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN || undefined,
    });
    updateLocalStorageLogo("logo", "");
    updateLocalStorageLogo("logo_alt", "");
    clearUserProfile();
    setIsLoggedIn(false);
    // Clear react-query cache
    queryClient.clear();

    window.$chatwoot?.reset(); // reset Chatwoot session
    window.$chatwoot?.toggleBubbleVisibility("hide");
    // Redirect to login page
    router.push("/login");
  }, [clearUserProfile, router, queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isLoggedIn,
        subscription,
        login,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
